import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import TypeAvatar from './TypeAvatar';
import { getTypeProfilePath } from '../data/founderTypes';
import { formatFounderTitle } from '../utils/textUtils';

const Card = styled(motion.div)`
  background-color: ${props => 
    props.isPrimary ? 'var(--surface-raised)' : 
    props.isSecondary ? 'var(--surface-raised)' : 
    'var(--surface-color)'};
  border-radius: var(--border-radius);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: ${props => props.showDetails ? '340px' : '220px'};
  border: 1px solid ${props => 
    props.isPrimary ? 'rgba(45, 212, 191, 0.35)' : 
    props.isSecondary ? 'var(--border-color)' : 
    'var(--border-color)'};
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: ${props => 
    props.isPrimary ? '0 10px 20px rgba(var(--primary-rgb), 0.1)' :
    props.isSecondary ? '0 10px 20px rgba(37, 65, 178, 0.1)' :
    'var(--box-shadow)'};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => 
      props.isPrimary ? '0 15px 30px rgba(var(--primary-rgb), 0.15)' :
      props.isSecondary ? '0 15px 30px rgba(37, 65, 178, 0.15)' :
      '0 10px 25px rgba(0, 0, 0, 0.2)'};
  }
`;

const TypeHeader = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid ${props => 
    props.isPrimary ? 'rgba(var(--primary-rgb), 0.2)' : 
    props.isSecondary ? 'rgba(37, 65, 178, 0.2)' : 
    'rgba(255, 255, 255, 0.1)'};
  gap: 1rem;
`;

const TypeTitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const TypeCode = styled.div`
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  color: ${props => props.$color || 'var(--primary-color)'};
  margin-bottom: 0.25rem;
`;

const TypeTitle = styled.h3`
  font-size: 1.4rem;
  margin: 0;
  color: ${props => 
    props.isPrimary ? 'var(--primary-color)' : 
    props.isSecondary ? 'var(--secondary-color)' : 
    'var(--text-primary)'};
  font-weight: 600;
  letter-spacing: -0.5px;
  flex: 1;
`;

const TypeDescription = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const SectionTitle = styled.h4`
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--primary-light);
  margin: 1rem 0 0.5rem;
  letter-spacing: 0.5px;
`;

const List = styled.ul`
  margin: 0;
  padding: 0 0 0 1.2rem;
  
  li {
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
    color: var(--text-secondary);
  }
`;

const ScoreBarContainer = styled.div`
  margin-top: auto;
  margin-bottom: 0.5rem;
  width: 100%;
`;

const ScoreBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  margin: 4px 0;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const ScoreFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: ${props => `${props.percentage}%`};
  background: var(--accent-solid, var(--primary-color));
  border-radius: 4px;
  transition: width 1s ease-out;
`;

const ScoreLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-top: 6px;
  
  strong {
    font-weight: 600;
  }
`;

const PrimaryBadge = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  background: var(--primary-color);
  color: #0C0F14;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(var(--primary-rgb), 0.3);
`;

const SecondaryBadge = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  background: var(--surface-raised);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(37, 65, 178, 0.3);
`;

const ProfileLink = styled(Link)`
  display: inline-block;
  margin-top: 0.75rem;
  font-size: 0.85rem;
  color: var(--text-secondary);

  &:hover {
    color: var(--primary-color);
  }
`;

const FounderTypeCard = ({ 
  type, 
  score,
  scoreLabel, 
  isPrimary = false, 
  isSecondary = false,
  showDetails = false
}) => {
  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  // Add safety check for type
  if (!type) {
    return null; // Return nothing if type is not defined
  }

  return (
    <Card
      isPrimary={isPrimary}
      isSecondary={isSecondary}
      showDetails={showDetails}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ y: -5 }}
    >
      {isPrimary && <PrimaryBadge>Your type</PrimaryBadge>}
      {isSecondary && <SecondaryBadge>Also you</SecondaryBadge>}

      <TypeHeader isPrimary={isPrimary} isSecondary={isSecondary}>
        <TypeAvatar type={type} size={48} animate={false} showCode={false} />
        <TypeTitleBlock>
          {type.code && <TypeCode $color={type.color}>{type.code}</TypeCode>}
          <TypeTitle isPrimary={isPrimary} isSecondary={isSecondary}>
            {formatFounderTitle(type.title) || 'Founder type'}
          </TypeTitle>
        </TypeTitleBlock>
      </TypeHeader>

      <TypeDescription>
        {isPrimary || isSecondary || showDetails 
          ? type.description 
          : type.shortDescription || (type.description && type.description.split('.')[0] + '.') || 'Description not available.'}
      </TypeDescription>

      {showDetails && (
        <>
          <SectionTitle>Strengths</SectionTitle>
          <List>
            {type.strengths && type.strengths.length > 0 
              ? type.strengths.map((strength, idx) => (
                  <li key={idx}>{strength}</li>
                ))
              : <li>Strengths information not available</li>
            }
          </List>

          <SectionTitle>Challenges</SectionTitle>
          <List>
            {type.challenges && type.challenges.length > 0
              ? type.challenges.map((challenge, idx) => (
                  <li key={idx}>{challenge}</li>
                ))
              : <li>Challenges information not available</li>
            }
          </List>
        </>
      )}

      <ScoreBarContainer>
        <ScoreLabel>
          <span>
            {scoreLabel
              || (score === 100 ? 'Compatibility' : isPrimary ? 'Primary influence' : isSecondary ? 'Secondary influence' : 'Type influence')}
          </span>
          <strong style={{ color: score > 70 ? 'var(--primary-color)' : 'inherit' }}>
            {score}%
          </strong>
        </ScoreLabel>
        <ScoreBar>
          <ScoreFill percentage={score} />
        </ScoreBar>
      </ScoreBarContainer>

      {type.id && (
        <ProfileLink to={getTypeProfilePath(type)}>
          Read the full {type.title.replace('The ', '').toLowerCase()} profile →
        </ProfileLink>
      )}
    </Card>
  );
};

export default FounderTypeCard; 